import { ConsentDisplay } from '@/components/consent-display';
import { ParentalConsentImage } from '@/components/registration/consent-step';
import { GeneralErrorDialog } from '@/components/registration/general-error-dialog';
import { routes } from '@/utils/routes';
import { DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { AccountConsent } from '@calm/evexia-web-sdk';
import consentStepMessages from '@rhg/components/registration/consent-step/messages';
import { logger } from '@rhg/libs/logger';
import {
    ConsentDialogContext,
    ConsentDialogOptions,
} from '@rhg/providers/consent-dialog-provider';
import { useRouter } from 'next/router';
import { useContext, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useGetCircles } from '../use-circles';
import { ConsentRequirement } from '../use-consent-context/types';
import useHasAcceptedCohortTos from '../use-has-accepted-tos';
import { useHistory } from '../use-history';
import { useMostRecentSponsorship } from '../use-most-recent-sponsorship';
import { useUpdateConsent } from '../use-update-consent';

export const useConsentDialog = () => {
    return useContext(ConsentDialogContext);
};

interface GetParentalConsentDialogOptionsProps {
    consentRequirements: ConsentRequirement;
    accountConsents: AccountConsent[];
}

const styleTitle = {
    mb: DS_SPACING_UNITS.SECTION,
};

export const useGetParentalConsentDialogOptions = () => {
    const { formatMessage } = useIntl();
    const { getCirclesAsync } = useGetCircles();
    const { updateConsentAsync } = useUpdateConsent();
    const { push } = useRouter();
    const { refetch: refetchConsent } = useHasAcceptedCohortTos();
    const { refetch: refetchMostRecentSponsorship } =
        useMostRecentSponsorship();
    const [error, setError] = useState(false);
    const triggerCloseRef = useRef<() => void>();
    const { back } = useHistory();

    const getParentalConsentDialogOptions = ({
        consentRequirements,
        accountConsents,
    }: GetParentalConsentDialogOptionsProps): ConsentDialogOptions => {
        const onAccept = async () => {
            try {
                await updateConsentAsync({
                    consentContext: consentRequirements.parental,
                    accountConsentId: accountConsents[0].consentId,
                });
                // Refetch synchoronously before moving on to next page
                await refetchConsent();
                await refetchMostRecentSponsorship();
                // Force refresh circles and sponsorships
                await getCirclesAsync(true);

                // set the underlying page to wherever the user was before the dialog opened
                back(routes.profile);

                // This ref allows the consumer to trigger the close from the correct button passed in. Having the consumer manage the isOpen state led to issues with the dialog not closing properly.
                triggerCloseRef.current?.();
            } catch (_error) {
                setError(true);
                // Log the error for debugging purposes
                logger.error(
                    'Error from accepting parental consent',
                    {},
                    _error as Error
                );
            }
        };

        return {
            trackDialog: 'NotMapped',
            title: formatMessage(consentStepMessages.parentalConsentHeading),
            iconColor: 'primary',
            fullScreen: true,
            triggerCloseRef,
            SimpleDialogProps: {
                hasGradientBackground: true,
                titleStyle: styleTitle,
                titleWrapperStyle: { mb: 0 },
                contentContainerStyle: { pt: 10 },
            },
            buttons: [
                {
                    title: formatMessage(consentStepMessages.accept),
                    color: 'primary',
                    // TODO: In EVE-8765, Add analytics to button
                    trackButton: 'Accept',
                    onClick: onAccept,
                },
            ],
            content: (
                <>
                    <ConsentDisplay
                        consentContext={consentRequirements.parental}
                        title={formatMessage(
                            consentStepMessages.parentalConsentHeading
                        )}
                        imageOverride={<ParentalConsentImage />}
                        srTitle={formatMessage(
                            consentStepMessages.parentalConsentHeading
                        )}
                        fallbackUrl={routes.profile}
                    />
                    {error && (
                        <GeneralErrorDialog
                            onClose={() => push(routes.profile)}
                            showSupportEmail={true}
                            showSupportMessage={true}
                        />
                    )}
                </>
            ),
        };
    };

    return {
        getParentalConsentDialogOptions,
    };
};
